import { ampli } from 'lib-shared-tracking';
import type { AsyncStatus } from 'lib-shared-types-api';
import { useEffect, useMemo, useState } from 'react';
import { useUnmount } from 'react-use';

import { analyticsApi } from 'apis/analytics';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { analyticsActions } from '../../../../analytics.actions';

import { UseQuickViewInitResult } from './useQuickViewInit.types';

/**
 * A hook for fetching a quick view based on the dashboard slug and quick view
 * id in the URL path params. First the dashboard matching the slug is looked
 * up, then the quick view is fetched from the API with the matching dashboard
 * id and the quick view id from the URL. If the quick view exists, we overwrite
 * the existing parameters in the Redux state and store a snapshot of the
 * previous state, to which we return to when leaving the quick view.
 */
export const useQuickViewInit = (): UseQuickViewInitResult => {
  const dispatch = useAppDispatch();
  const params = useAppPathParams('/analytics/dashboard/:slug/quick-view/:id');

  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>('uninitialized');

  const [readQuickViewQuery, { quickView }] =
    analyticsApi.useLazyReadQuickViewQuery({
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        quickView: data?.data.quick_view,
      }),
    });

  const { dashboard } = analyticsApi.endpoints.readDashboards.useQueryState(
    undefined,
    {
      selectFromResult: ({ data }) => ({
        dashboard: data?.data.dashboards.find(
          (dashboard) => dashboard.slug === params.slug,
        ),
      }),
    },
  );

  useEffect(() => {
    const readQuickView = async () => {
      if (dashboard) {
        setAsyncStatus('pending');

        const {
          data: { quick_view: quickView },
        } = await readQuickViewQuery({
          dashboardId: dashboard.id,
          id: params.id,
        }).unwrap();

        if (quickView) {
          ampli.analyticsQuickViewViewed({
            dashboard: dashboard.title,
            shared: quickView.shared,
          });

          dispatch(analyticsActions.setQuickView({ dashboard, quickView }));
          setAsyncStatus('fulfilled');
        }
      }
    };

    readQuickView().catch(() => {
      setAsyncStatus('rejected');
    });
    // Only run the effect when the URL path params change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, params.slug]);

  // Restores the previously saved dashboard snapshot when leaving the quick view.
  useUnmount(() => {
    dispatch(analyticsActions.restoreSnapshot());
  });

  return useMemo(
    () => ({
      quickView,
      asyncStatus,
    }),
    [quickView, asyncStatus],
  );
};
